import React, { useMemo, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import { Box, useTheme, useMediaQuery } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid'

import { getActiveDeactivatedLocations } from 'utils/location'
import { get } from 'utils/lodash'
import { useLazyGetLocationsNotesCountQuery } from 'api/notes/getLocationsNotesCount'
import { selectAddressName } from 'data/address/addressSelectors'
import { combineAddressInfo } from 'utils/helper'
import { useActivateDeactivateLocationsMutation } from 'api/location/activateDeactivateLocations'
import Loader from 'components/common/loader'
import { LAYOUT_HEIGHT } from 'components/customer-details/layout/settings'
import {
  setExpandedRowIds,
  setSelectedLocationForMenu,
  setLocationAnchorEl,
  setMoreActionsAnchorEl,
  setIsOpenAddLocationDialog,
  setSelectedTab,
} from 'slices/customer/customerDetailsLocationsSlice'
import {
  selectSelectedTab,
  selectExpandedRowIds,
  selectSelectedLocationForMenu,
  selectLocationAnchorEl,
  selectMoreActionsAnchorEl,
  selectIsOpenAddLocationDialog,
} from 'data/customer/customerDetailsLocationsSelectors'
import AddEditLocationDialog from '../add-edit-dialog'
import LocationActionMenu from '../LocationActionMenu'
import { ACTIVE, DEACTIVATED } from './settings'
import { useDataGridInteractions } from './hooks/useDataGridInteractions'
import useLocationVacationHolds from './hooks/useLocationVacationHolds'
import LocationsDataGrid from './components/LocationsDataGrid'
import LocationsDataGridActionsMenu from './components/LocationsDataGridActionsMenu'

const LocationsList = ({
  customerDetails = {},
  accountId,
  selectedLocationId = '',
  onLocationChange,
  onLocationStatusChange,
  onSaveTags,
  showAddLocationIcon = true,
}) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isTabletorMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const apiRef = useGridApiRef()
  const pendingTabChangeRef = useRef(false)

  const selectedTab = useSelector(selectSelectedTab)
  const expandedRowIds = useSelector(selectExpandedRowIds)
  const selectedLocationForMenu = useSelector(selectSelectedLocationForMenu)
  const locationAnchorEl = useSelector(selectLocationAnchorEl)
  const moreActionsAnchorEl = useSelector(selectMoreActionsAnchorEl)
  const isOpenAddLocationDialog = useSelector(selectIsOpenAddLocationDialog)

  // eslint-disable-next-line no-unused-vars
  const [_, { isLoading: isDeactivateAllLoading }] = useActivateDeactivateLocationsMutation()
  const [getLocationsNotesCount, { data: notesCountData }] = useLazyGetLocationsNotesCountQuery()
  const allLocationsNotesCount = get(notesCountData, 'counts', [])

  const { activeLocations, deactivatedLocations } = getActiveDeactivatedLocations(customerDetails, true)

  const { getVacationHoldCountForLocation, getRunningVacationHolds } = useLocationVacationHolds(accountId)

  const deactivatableLocations = useMemo(() => {
    return activeLocations.filter(location => get(location, 'serviceCount', 0) === 0)
  }, [activeLocations])

  const activatableLocations = useMemo(() => {
    return deactivatedLocations
  }, [deactivatedLocations])

  const currentTabLocations = useMemo(() => {
    return selectedTab === ACTIVE ? activeLocations : deactivatedLocations
  }, [selectedTab, activeLocations, deactivatedLocations])

  useEffect(() => {
    if (selectedLocationId && deactivatedLocations.some(location => location.id === selectedLocationId)) {
      dispatch(setSelectedTab(DEACTIVATED))
    } else if (selectedLocationId && activeLocations.some(location => location.id === selectedLocationId)) {
      dispatch(setSelectedTab(ACTIVE))
    }
  }, [])

  const rows = useMemo(() => {
    return currentTabLocations.map(location => {
      const address = get(location, 'address', {})
      const addressName = selectAddressName(address)
      const fullAddress = combineAddressInfo(address)
      const vacationHoldCount = getVacationHoldCountForLocation(location.id)
      const runningVacationHolds = getRunningVacationHolds(location.id)
      const currentLocationNotesCount = allLocationsNotesCount.find(loc => loc.locationId === location.id)

      return {
        ...location,
        locationId: location.id,
        addressName,
        fullAddress,
        vacationHoldCount,
        isOnVacationHold: runningVacationHolds.length > 0,
        archivedNotesCount: get(currentLocationNotesCount, 'archivedNotesCount', 0),
        notesCount: get(currentLocationNotesCount, 'notesCount', 0),
      }
    })
  }, [currentTabLocations, getVacationHoldCountForLocation, allLocationsNotesCount, getRunningVacationHolds])

  const { handleDetailPanelExpandedRowIdsChange, handleRowClick, getRowClassName } = useDataGridInteractions({
    selectedLocationId,
    onLocationChange,
    selectedTab,
    setExpandedRowIds: ids => dispatch(setExpandedRowIds(ids)),
    rows,
    apiRef,
  })

  const handleTabChange = useCallback(() => {
    pendingTabChangeRef.current = true
  }, [])

  useEffect(() => {
    if (!pendingTabChangeRef.current) return
    pendingTabChangeRef.current = false

    if (apiRef?.current) {
      const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef)
      onLocationChange(filteredSortedRowIds.length > 0 ? filteredSortedRowIds[0] : '')
    }
  }, [selectedTab, rows, apiRef, onLocationChange])

  const handleLocationStatusChange = useCallback(
    locationId => {
      if (locationId === selectedLocationId) {
        onLocationChange('')
      }
      onLocationStatusChange?.()
    },
    [selectedLocationId, onLocationChange, onLocationStatusChange]
  )

  const handleCloseMoreActionsMenu = () => {
    dispatch(setMoreActionsAnchorEl(null))
  }

  const handleLocationAnchorElChange = el => {
    dispatch(setLocationAnchorEl(el))
  }

  const handleSelectedLocationForMenuChange = location => {
    dispatch(setSelectedLocationForMenu(location))
  }

  const onCloseLocationActionMenu = () => {
    dispatch(setLocationAnchorEl(null))
    dispatch(setSelectedLocationForMenu(null))
  }

  const onLocationActionMenuStatusChange = () => {
    handleLocationStatusChange(selectedLocationForMenu?.id)
    dispatch(setLocationAnchorEl(null))
    dispatch(setSelectedLocationForMenu(null))
  }

  const handleCloseAddLocationDialog = () => {
    dispatch(setIsOpenAddLocationDialog(false))
  }

  useEffect(() => {
    getLocationsNotesCount({ accountId })
  }, [getLocationsNotesCount, accountId])

  return (
    <>
      {isDeactivateAllLoading && <Loader />}
      <Box
        display="flex"
        flexDirection="column"
        height={isTabletorMobile ? '100%' : LAYOUT_HEIGHT}
        sx={{
          overflow: 'hidden',
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          width: '100%',
        }}
      >
        <Box flex={1} display="flex" flexDirection="column" sx={{ overflow: 'hidden' }}>
          <LocationsDataGrid
            rows={rows}
            expandedRowIds={expandedRowIds}
            onSaveTags={onSaveTags}
            accountId={accountId}
            onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
            onRowClick={handleRowClick}
            getRowClassName={getRowClassName}
            onLocationAnchorElChange={handleLocationAnchorElChange}
            onSelectedLocationForMenuChange={handleSelectedLocationForMenuChange}
            apiRef={apiRef}
            customerDetails={customerDetails}
            showAddLocationIcon={showAddLocationIcon}
            onLocationStatusChange={onLocationStatusChange}
            onTabChange={handleTabChange}
          />
        </Box>

        <AddEditLocationDialog isOpen={isOpenAddLocationDialog} accountId={accountId} onClose={handleCloseAddLocationDialog} />

        <LocationActionMenu
          anchorEl={locationAnchorEl}
          selectedLocation={selectedLocationForMenu}
          accountId={accountId}
          onClose={onCloseLocationActionMenu}
          onLocationStatusChange={onLocationActionMenuStatusChange}
          showArchivedNotesOption
        />

        <LocationsDataGridActionsMenu
          anchorEl={moreActionsAnchorEl}
          onClose={handleCloseMoreActionsMenu}
          deactivatableLocations={deactivatableLocations}
          activatableLocations={activatableLocations}
          onLocationStatusChange={onLocationStatusChange}
        />
      </Box>
    </>
  )
}

LocationsList.propTypes = {
  customerDetails: PropTypes.object,
  accountId: PropTypes.string.isRequired,
  selectedLocationId: PropTypes.string,
  onLocationChange: PropTypes.func.isRequired,
  onLocationStatusChange: PropTypes.func,
  onSaveTags: PropTypes.func,
  showAddLocationIcon: PropTypes.bool,
}

export default LocationsList
